import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { cache } from '@common/util/cache.operator';
import { isEnumValue } from '@common/util/enum';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { PubSubService } from '@portal-core/general/services/pub-sub.service';
import { TeamAccessFormTab } from '@portal-core/teams/components/team-access-form/team-access-form.component';
import { TeamProfileComponent, TeamProfileForm } from '@portal-core/teams/components/team-profile/team-profile.component';
import { TeamProfileTab } from '@portal-core/teams/enums/team-profile-tab.enum';
import { Team } from '@portal-core/teams/models/team.model';
import { TeamsService } from '@portal-core/teams/services/teams.service';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { LoadingState } from '@portal-core/util/loading-state';
import { BehaviorSubject, Observable, first, switchMap } from 'rxjs';

export interface TeamProfileDialogData {
  teamAccessFormTab?: TeamAccessFormTab;
  teamId: number;
  teamProfileTab?: TeamProfileTab;
}

@Component({
  selector: 'mc-team-profile-dialog',
  templateUrl: './team-profile-dialog.component.html',
  styleUrls: ['./team-profile-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamProfileDialogComponent extends DialogBase implements OnInit {
  static DialogConfig: MatDialogConfig = {
    width: '95rem',
    height: '79rem',
    autoFocus: false
  };

  @ViewChild(TeamProfileComponent, { static: true }) teamProfileComponent: TeamProfileComponent;

  public get requirePromptOnClose(): boolean {
    return this.teamProfileComponent?.requirePromptOnClose;
  }

  loadingState: LoadingState<string> = new LoadingState<string>();
  reload$: BehaviorSubject<void> = new BehaviorSubject(undefined);
  teamAccessFormTab: TeamAccessFormTab;
  team$: Observable<Team>;
  teamProfileTab: TeamProfileTab;
  needsDelayedReload: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: TeamProfileDialogData,
    protected dialog: MatDialog,
    protected dialogRef: MatDialogRef<TeamProfileDialogComponent>,
    private errorService: ErrorService,
    private teamsService: TeamsService,
    private pubSubService: PubSubService
  ) {
    super(dialog, dialogRef);
  }

  ngOnInit() {
    super.ngOnInit();

    this.loadingState.update(true);

    // Create an observable of the team. Start with reload$ so that the data can be reloaded as needed
    this.team$ = this.reload$.pipe(
      switchMap(() => this.teamsService.getItemById$(this.data.teamId, { forceApiRequest: true })),
      cache()
    );

    this.team$.pipe(
      first(),
    ).subscribe(() => {
      // Set the initial tab
      if (isEnumValue(TeamProfileTab, this.data.teamProfileTab)) {
        this.teamProfileTab = this.data.teamProfileTab;
      } else {
        this.teamProfileTab = TeamProfileTab.Access;
      }

      this.teamAccessFormTab = this.data.teamAccessFormTab;

      this.loadingState.update(false);
      this.onLoaded();
    }, error => {
      this.loadingState.update(false, 'Unable to load the team\'s profile.', this.errorService.getErrorMessages(error));
    });

    this.dialogRef.afterClosed().pipe(first()).subscribe(dialog => {
      if (this.needsDelayedReload) {
        this.pubSubService.publish('team-updated', { teamId: this.data.teamId });
      }
    });
  }

  onSaved(teamProfileForm: TeamProfileForm) {
    switch (teamProfileForm) {
      case TeamProfileForm.Settings:
        this.reload();
        break;
      case TeamProfileForm.Access:
        this.needsDelayedReload = true;
        break;
      case TeamProfileForm.Delete:
        this.closeDialog();
        break;
    }
  }

  onCancel() {
    this.closeDialog();
  }

  reload() {
    this.reload$.next();
  }
}
